import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus, Upload, Signature } from "lucide-react";
import VoiceInput from "@/components/VoiceInput";
import axios from "axios";

const DOC_NUMERIC = { mobile: 10 };
const DOC_NUM_INT = new Set(["opd_charge","ipd_charge","emergency_charge","surgeon_charge","opd_commission","ipd_commission","ot_commission"]);

const empty = { name: "", department: "", qualification: "", reg_no: "", mobile: "", email: "", opd_charge: 500, ipd_charge: 400, emergency_charge: 1500, surgeon_charge: 0, opd_commission: 60, ipd_commission: 60, ot_commission: 60, status: "active", photo: "" };

export default function Doctors() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [sigV, setSigV] = useState(0);       // cache-buster after upload
  const [sigBusy, setSigBusy] = useState(false);
  const sigFileRef = useRef(null);
  const canEdit = ["admin", "super_admin"].includes(user?.role);

  const load = () => api.get("/doctors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const uploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Signature must be under 2 MB"); return; }
    setSigBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${editing.id}/signature`, fd, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      toast.success("Signature uploaded");
      setSigV(Date.now());
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setSigBusy(false); if (sigFileRef.current) sigFileRef.current.value = ""; }
  };

  const removeSignature = async () => {
    if (!editing) return;
    if (!window.confirm("Remove digital signature? Rx/discharge will fall back to the printed line.")) return;
    try {
      await api.delete(`/doctors/${editing.id}/signature`);
      toast.success("Signature removed");
      setSigV(Date.now());
    } catch { toast.error("Failed"); }
  };

  const filtered = rows.filter((d) =>
    (!q || d.name.toLowerCase().includes(q.toLowerCase())) &&
    (!dept || d.department === dept)
  );
  const depts = [...new Set(rows.map((d) => d.department))];

  const save = async () => {
    if (!form.name || !form.department) return toast.error("Name and department required");
    try {
      if (editing) { await api.put(`/doctors/${editing.id}`, form); toast.success("Doctor updated"); }
      else { await api.post("/doctors", form); toast.success("Doctor added"); }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (d, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${d.name}? This can't be undone.`)) return;
    try { await api.delete(`/doctors/${d.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const startEdit = (d, e) => { e.stopPropagation(); setEditing(d); setForm({ ...empty, ...d }); setOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Doctors</h1>
          <p className="text-sm text-slate-500">{filtered.length} doctors across specialties.</p>
        </div>
        {canEdit && <Button data-testid="add-doctor-btn" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-blue-700 hover:bg-blue-800 rounded-sm"><UserPlus className="w-4 h-4 mr-1"/> Add Doctor</Button>}
      </div>

      <div className="bg-white border p-3 flex gap-2 flex-wrap">
        <Input placeholder="Search doctor" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" />
        <select value={dept} onChange={(e) => setDept(e.target.value)} className="border h-9 text-sm px-2"><option value="">All Departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <div key={d.id} onClick={() => nav(`/doctors/${d.id}`)} data-testid={`doc-${d.name.replace(/\s/g,'-')}`} className="bg-white border border-slate-200 p-4 hover:border-blue-500 cursor-pointer transition">
            <div className="flex gap-3">
              <img src={d.photo || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100"} alt="" className="w-14 h-14 object-cover rounded-sm" />
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-900">{d.name}</div>
                <div className="text-xs text-slate-500">{d.department}</div>
                <div className="text-[11px] text-slate-500 mt-1">{d.qualification}</div>
              </div>
              <Badge className={`rounded-sm ${d.status === "active" ? "bg-teal-100 text-teal-800" : "bg-slate-100"}`}>{d.status}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div><span className="label-tiny">OPD</span><div className="font-mono">{fmtINR(d.opd_charge)}</div></div>
              <div><span className="label-tiny">IPD</span><div className="font-mono">{fmtINR(d.ipd_charge)}</div></div>
              <div><span className="label-tiny">Emergency</span><div className="font-mono">{fmtINR(d.emergency_charge)}</div></div>
              <div><span className="label-tiny">Comm OPD</span><div className="font-mono">{d.opd_commission}%</div></div>
              <div><span className="label-tiny">Comm IPD</span><div className="font-mono">{d.ipd_commission}%</div></div>
              <div><span className="label-tiny">Comm OT</span><div className="font-mono">{d.ot_commission}%</div></div>
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-3 border-t pt-3">
                <button data-testid={`edit-doc-${d.name.replace(/\s/g,'-')}`} onClick={(e) => startEdit(d, e)} className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-100 rounded-sm flex items-center gap-1"><Pencil className="w-3 h-3"/> Edit</button>
                <button data-testid={`del-doc-${d.name.replace(/\s/g,'-')}`} onClick={(e) => del(d, e)} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm flex items-center gap-1"><Trash2 className="w-3 h-3"/> Remove</button>
                <button onClick={(e) => { e.stopPropagation(); nav(`/doctors/${d.id}`); }} className="text-xs px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm ml-auto">View Profile →</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name}` : "Add New Doctor"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            {[["name","Name"],["department","Department"],["qualification","Qualification"],["reg_no","Registration No"],["mobile","Mobile"],["email","Email"],["photo","Photo URL"],["opd_charge","OPD Charge"],["ipd_charge","IPD Charge"],["emergency_charge","Emergency Charge"],["surgeon_charge","Surgeon Charge"],["opd_commission","OPD Commission %"],["ipd_commission","IPD Commission %"],["ot_commission","OT Commission %"]].map(([k,l]) => (
              <div key={k}>
                <Label className="label-tiny">{l}</Label>
                {DOC_NUM_INT.has(k) ? (
                  <Input type="number" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })} className="mt-1 h-9" />
                ) : (
                  <VoiceInput
                    as="input"
                    className="mt-1"
                    numeric={DOC_NUMERIC[k] !== undefined}
                    maxLength={DOC_NUMERIC[k]}
                    value={form[k] || ""}
                    onChange={(v) => setForm({ ...form, [k]: v })}
                  />
                )}
              </div>
            ))}
            <div>
              <Label className="label-tiny">Status</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 h-9 border w-full px-2 text-sm"><option>active</option><option>inactive</option></select>
            </div>
          </div>
          {form.photo && <div className="text-center"><img src={form.photo} alt="preview" className="h-20 w-20 object-cover mx-auto rounded-sm"/></div>}
          {editing && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-2" data-testid="doc-signature-card">
              <div className="label-tiny flex items-center gap-1 mb-2"><Signature className="w-3.5 h-3.5"/> Digital Signature · auto-printed on Rx & Discharge</div>
              <div className="flex items-center gap-3">
                <div className="w-40 h-16 bg-white border border-dashed border-slate-300 rounded-sm grid place-items-center overflow-hidden">
                  <img
                    key={sigV}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${editing.id}/signature?v=${sigV || editing.signature_updated_at || 0}`}
                    alt="Signature"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      // Hide the broken image and show a plain-text fallback.
                      // Uses textContent (never innerHTML) so no HTML can ever
                      // be injected into the DOM.
                      e.target.style.display = "none";
                      const parent = e.target.parentElement;
                      if (parent && !parent.querySelector(".sig-fallback")) {
                        const s = document.createElement("span");
                        s.className = "sig-fallback text-[10px] text-slate-400";
                        s.textContent = "No signature";
                        parent.appendChild(s);
                      }
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <input ref={sigFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadSignature} className="hidden" data-testid="doc-sig-file"/>
                  <Button variant="outline" size="sm" disabled={sigBusy} onClick={() => sigFileRef.current?.click()} className="rounded-sm" data-testid="doc-sig-upload">
                    <Upload className="w-3.5 h-3.5 mr-1"/> {sigBusy ? "Uploading…" : "Upload Signature"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={removeSignature} className="rounded-sm text-red-700 hover:bg-red-50" data-testid="doc-sig-remove">
                    <Trash2 className="w-3.5 h-3.5 mr-1"/> Remove
                  </Button>
                  <div className="text-[10px] text-slate-500">PNG / JPG / SVG · transparent background recommended · ≤ 2 MB</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="doc-save" onClick={save} className="bg-blue-700 hover:bg-blue-800">{editing ? "Save Changes" : "Add Doctor"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
